import { useEffect, useState, type FormEvent } from 'react';
import { VoicePicker } from '../components/VoicePicker';
import { Link, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { patch, post } from '../api/client';
import { isMissing, qk, stopRobots, useCatalog, useRobotTours } from '../api/queries';
import type { Lang, PerRobotResult, Tour } from '../api/types';
import { PageHeader } from '../components/Layout';
import { IconBolt, IconCamera, IconMic, IconMuted, IconSpeaker, IconWave } from '../components/Icon';
import { PoseMiniMap } from '../components/MiniMap';
import { Battery, Modal } from '../components/ui';
import { RobotAudio, RobotVideo } from '../components/Video';
import { HeadPanel, LightControl, VolumeControl } from '../components/RobotControls';
import { Select } from '../components/Select';
import { ScreenControl } from '../components/ScreenControl';
import { StuckBanner } from '../components/Stuck';
import { RobotTabs } from '../components/RobotTabs';
import { ModeChip } from '../components/ModeChip';
import { RobotSiteChip } from '../components/Site';
import { UpdateChip } from '../components/UpdateChip';
import { HandPushFab } from '../components/HandPushFab';
import { RobotLogs } from '../components/RobotLogs';
import { SensorMini } from '../components/SensorMini';
import { AutoChargeCard, ChargeBanner, DockMenu } from '../components/Charge';
import { useDockTitle } from '../lib/charge';
import { HandPushBanner, HandPushButton } from '../components/HandPush';
import { GuardPanel } from '../components/Guard';
import { useHandPush } from '../lib/handPush';
import { AutonomyCaption, AutonomyHeaderControl, AutonomySide, AutonomyStateChip, AutonomyStats, AutonomyTranscript } from '../components/Autonomy';
import { useAutonomy } from '../lib/autonomy';
import { LocalizationActions, LocalizationState } from '../components/Localization';
import { blockedByCharging } from '../lib/localization';
import { perRobotToast, toast, toastErr } from '../components/toast';
import { useT, type MsgKey } from '../i18n';
import { startAudio, useMedia, useWatch, type VideoSource } from '../live/media';
import { useLive, type RobotLogEntry } from '../live/store';
import { useKeyDrive, type DriveKey } from '../lib/drive';
import { ago, timeStr } from '../lib/format';
import { useAuthedImage, useStoredState } from '../lib/hooks';
import { rememberFocus } from '../lib/selection';
import { LANGS, openSetPose, sendCmd } from '../lib/robot';
import { talk } from '../lib/talk';

const QUICK_MOTIONS: { id: string; key: MsgKey }[] = [
  { id: 'goodbye', key: 'motion.wave' },
  { id: 'nod', key: 'motion.nod' },
  { id: 'applause', key: 'motion.applause' },
  { id: 'guideleft', key: 'motion.guideLeft' },
  { id: 'guideright', key: 'motion.guideRight' },
  { id: 'hug', key: 'motion.hug' },
];

export default function RobotPage() {
  const { serial = '' } = useParams();
  const t = useT();
  const robot = useLive((s) => s.robots[serial]);
  const info = useMedia((s) => s.byRobot[serial]);
  const [vol, setVol] = useStoredState('cruzr.vol', 0.8);
  const [source, setSource] = useStoredState<VideoSource>('cruzr.robotSource', 'camera');
  const dockTitle = useDockTitle(robot?.status?.currentMap);
  const [radarOn, setRadarOn] = useStoredState('cruzr.videoRadar', true);
  const [lidarOn, setLidarOn] = useStoredState('cruzr.videoLidar', true);
  const [control, setControl] = useState(false);
  const auto = useAutonomy(serial);
  // While autonomy runs the right side shows the transcript; "take over" switches back to the manual controls.
  const [manual, setManual] = useState(false);
  const showAutoSide = auto.active && !manual;
  const controlling = control && source === 'screen' && Boolean(robot?.online);
  const [muted, setMuted] = useStoredState('cruzr.muted', false);
  const [snap, setSnap] = useState<string | null>(null);
  const [snapping, setSnapping] = useState(false);
  useWatch(serial, Boolean(robot?.online), source);
  useEffect(() => {
    if (serial) rememberFocus(serial);
  }, [serial]);
  // Stop talking when leaving the view.
  useEffect(() => () => void talk(serial, false), [serial]);

  if (!robot) {
    return (
      <>
        <PageHeader title={serial} />
        <div className="empty">{useLive.getState().wsState === 'open' ? t('robot.unknown') : t('common.loading')}</div>
      </>
    );
  }
  const st = robot.status ?? {};
  const ip = robot.androidIp;
  const fw = robot.firmware ? robot.firmware.match(/\d+\.\d+/)?.[0] ?? robot.firmware : null;

  const snapshot = async () => {
    setSnapping(true);
    const d = (await sendCmd(serial, 'snapshot', { maxWidth: 1280 })) as { url?: string } | null;
    setSnapping(false);
    if (d?.url) setSnap(d.url);
  };
  const rename = async () => {
    const name = prompt(t('robot.renamePrompt'), robot.alias ?? robot.name);
    if (name === null) return;
    try {
      await patch(`/api/robots/${encodeURIComponent(serial)}`, { name: name.trim() || null });
    } catch (e) {
      toastErr(e);
    }
  };

  return (
    <>
      <PageHeader
        title={
          <h1 className="page-title ellipsis" title={t('robot.rename')} onDoubleClick={() => void rename()}>
            {robot.name}
          </h1>
        }
      >
        <RobotSiteChip robot={robot} />
        <span className="row" style={{ gap: 6, fontSize: 13, color: robot.online ? 'var(--green)' : 'var(--muted)', flexShrink: 0 }}>
          <span className={`dot ${robot.online ? 'green' : ''}`} />
          {robot.online ? t('robot.online') : t('robot.offlineSeen', { ago: ago(robot.lastSeen) })}
        </span>
        <span className="mono ellipsis hide-mobile" style={{ fontSize: 12, color: 'var(--muted)' }}>
          {[robot.serial, fw && `fw ${fw}`, ip].filter(Boolean).join(' · ')}
        </span>
        <UpdateChip robot={robot} />
        <RobotTabs serial={serial} active="drive" />
        {robot.online && <ModeChip serial={serial} />}
        <div className="grow" />
        <Link to={`/drive/${encodeURIComponent(serial)}`} className="btn sm only-mobile">
          {t('robot.phoneDrive')}
        </Link>
        <AutonomyHeaderControl serial={serial} online={robot.online} />
        <Battery pct={st.battery} charging={st.charging} large />
      </PageHeader>
      <div className="robot-body">
        <section className="robot-video-col" aria-label={t('robot.liveVideo')}>
          <div className="robot-video">
            {robot.online ? (
              <RobotVideo
                serial={serial}
                online
                source={source}
                liveLabel={t('robot.liveWatching', { n: info?.viewers ?? 1 })}
                placeholder={t('robot.headCamera')}
                topExtra={
                  <>
                    {auto.active && <AutonomyStateChip state={auto.state} big />}
                    <div role="radiogroup" aria-label={t('sessions.source')} className="seg sm">
                      {(['camera', 'screen'] as VideoSource[]).map((s) => (
                        <button key={s} type="button" role="radio" aria-checked={source === s} onClick={() => setSource(s)}>
                          {t(s === 'camera' ? 'sessions.camera' : 'sessions.screen')}
                        </button>
                      ))}
                    </div>
                    {source === 'screen' && (
                      <button type="button" className={`overlay-btn sm${control ? ' on' : ''}`} aria-pressed={control} onClick={() => setControl(!control)} title={t('screenCtl.toggleTitle')}>
                        {t('screenCtl.toggle')}
                      </button>
                    )}
                  </>
                }
              />
            ) : (
              <>
                <div className="scan" />
                <span className="video-state">{t('robot.offlineSeen', { ago: ago(robot.lastSeen) })}</span>
              </>
            )}
            <RobotAudio serial={serial} enabled volume={vol} muted={muted} />
            {controlling && <ScreenControl serial={serial} />}
            {auto.active && !controlling && <AutonomyCaption serial={serial} />}
            {robot.online && !controlling && <StuckBanner serial={serial} />}
            {robot.online && !controlling && <ChargeBanner serial={serial} />}
            {robot.online && !controlling && <HandPushBanner serial={serial} />}
            {robot.online && !controlling && radarOn && <SensorMini serial={serial} size={170} lidar={lidarOn} className={`video-radar${auto.active ? ' raised' : ''}`} />}
            {st.currentMap && !controlling && <PoseMiniMap className="minimap-overlay" mapName={st.currentMap} location={st.location} />}
            {robot.online && (
              <div className="video-toolbar">
                <button type="button" className={`overlay-btn${info?.talking ? ' on' : ''}`} aria-pressed={Boolean(info?.talking)} disabled={info?.state !== 'connected'} onClick={() => void talk(serial, !info?.talking)} style={{ fontWeight: 600 }}>
                  <IconMic />
                  {info?.talking ? t('robot.talkingMute') : t('talk.talk')}
                </button>
                <button type="button" className="overlay-btn" onClick={() => void snapshot()} disabled={snapping}>
                  <IconCamera />
                  {snapping ? t('robot.snapping') : t('robot.snapshot')}
                </button>
                <button type="button" className={`overlay-btn${radarOn ? ' on' : ''}`} aria-pressed={radarOn} onClick={() => setRadarOn(!radarOn)} title={t('sens.radarToggleTitle')}>
                  {t('sens.radarToggle')}
                </button>
                {radarOn && (
                  <button type="button" className={`overlay-btn${lidarOn ? ' on' : ''}`} aria-pressed={lidarOn} onClick={() => setLidarOn(!lidarOn)} title={t('lidar.toggleTitle')}>
                    {t('lidar.toggle')}
                  </button>
                )}
                <label className="overlay-btn">
                  <button type="button" onClick={() => setMuted(!muted)} aria-label={muted ? t('robot.unmute') : t('robot.mute')} style={{ border: 'none', background: 'none', padding: 0, display: 'flex', color: muted ? 'var(--muted)' : 'var(--text)' }}>
                    {muted ? <IconMuted /> : <IconSpeaker />}
                  </button>
                  <span className="hide-mobile">{t('robot.robotSound')}</span>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.05}
                    value={muted ? 0 : vol}
                    aria-label={t('robot.volume')}
                    onChange={(e) => {
                      setVol(Number(e.target.value));
                      if (muted && Number(e.target.value) > 0) setMuted(false);
                    }}
                    style={{ width: 110, accentColor: 'var(--blue)' }}
                  />
                </label>
                {info && !info.canPlayAudio && (
                  <button type="button" className="overlay-btn on" onClick={() => void startAudio(serial)}>
                    {t('robot.enableAudio')}
                  </button>
                )}
                <div className="grow" />
                <VideoStats />
                <HandPushFab docked serial={serial} />
              </div>
            )}
            {!robot.online && <HandPushFab docked serial={serial} />}
          </div>
          {auto.active ? <AutonomyStats serial={serial} /> : <BottomLogs serial={serial} />}
        </section>
        {showAutoSide ? (
          <AutonomySide serial={serial} robotName={robot.name} onTakeOver={() => setManual(true)} />
        ) : (
        <aside className="robot-side" aria-label={t('robot.controls')}>
          {auto.active && (
            <div className="auto-banner" role="status">
              <AutonomyStateChip state={auto.state} />
              <span className="grow" style={{ fontSize: 13 }}>
                {t('auto.manualBanner')}
              </span>
              <button type="button" className="btn xs" onClick={() => setManual(false)}>
                {t('auto.backToTranscript')}
              </button>
            </div>
          )}
          <DrivePanel serial={serial} online={robot.online} />
          <HeadPanel serial={serial} online={robot.online} />
          <LocalizationPanel serial={serial} online={robot.online} mapName={st.currentMap ?? null} />
          <GuardPanel serial={serial} online={robot.online} />
          <SpeechPanel serial={serial} online={robot.online} />
          <QuickActions serial={serial} online={robot.online} />
          <ToursPanel serial={serial} online={robot.online} />
          <AutoChargeCard serial={serial} />
          <div className="robot-actions">
            {robot.status?.docked || robot.status?.charging ? (
              <button type="button" className="btn lg grow" style={{ background: 'var(--panel)' }} disabled={!robot.online} onClick={() => void sendCmd(serial, 'undock')}>
                <IconBolt />
                {t('fleet.leaveCharger')}
              </button>
            ) : robot.status?.task?.kind === 'dock' ? (
              <button type="button" className="btn lg grow" style={{ background: 'var(--panel)', color: '#FF8A80' }} onClick={() => void sendCmd(serial, 'stop')}>
                <IconBolt />
                {t('fleet.cancelCharger')}
              </button>
            ) : (
              <span className="split">
                <button type="button" className="btn lg grow" style={{ background: 'var(--panel)' }} disabled={!robot.online} title={dockTitle} onClick={() => void sendCmd(serial, 'dock')}>
                  <IconBolt />
                  <span className="btn-label">{t('charge.dockNearest')}</span>
                </button>
                <DockMenu serial={serial} mapName={st.currentMap} disabled={!robot.online} className="lg" onDock={(pointId) => void sendCmd(serial, 'dock', pointId ? { pointId } : {})} />
              </span>
            )}
            <HandPushButton serial={serial} online={robot.online} className="lg" />
            <button type="button" className="btn lg" style={{ background: 'var(--panel)' }} onClick={() => void rename()}>
              {t('robot.rename')}
            </button>
          </div>
        </aside>
        )}
      </div>
      {snap && <SnapshotModal url={snap} onClose={() => setSnap(null)} />}
    </>
  );
}

/** Localization state + Locate automatically / Set pose on map / Auto-locate (status.localization, setSettings autoLocate). */
function LocalizationPanel({ serial, online, mapName }: { serial: string; online: boolean; mapName: string | null }) {
  const t = useT();
  const st = useLive((s) => s.robots[serial]?.status);
  const reported = Boolean((st as { localization?: unknown } | null | undefined)?.localization);
  return (
    <section className="panel" style={{ gap: 10 }}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h2 className="h2">{t('loc.title')}</h2>
        {mapName && <span className="mono muted ellipsis" style={{ fontSize: 12 }}>{mapName}</span>}
      </div>
      <LocalizationState serial={serial} />
      <div className="row wrap" style={{ gap: 8 }}>
        <LocalizationActions serial={serial} online={online} size="sm" />
        <button type="button" className="btn sm" disabled={!online || !mapName} onClick={() => !blockedByCharging(serial) && void openSetPose(serial)}>
          {t('loc.setPoseOnMap')}
        </button>
      </div>
      {!reported && online && <span className="muted" style={{ fontSize: 11 }}>{t('loc.olderApp')}</span>}
    </section>
  );
}

/** Event log, plus the autonomy transcript next to it while autonomy is on (or has something to show). */
function BottomLogs({ serial }: { serial: string }) {
  // After autonomy ended, keep its transcript visible next to the event log until cleared.
  const hasTranscript = useLive((s) => Boolean(s.autonomyLog[serial]?.length));
  if (!hasTranscript) return <RobotLogs serial={serial} events={<EventLog serial={serial} />} />;
  return (
    <div className="robot-bottom">
      <RobotLogs serial={serial} events={<EventLog serial={serial} />} />
      <AutonomyTranscript serial={serial} />
    </div>
  );
}

function VideoStats() {
  const [dims, setDims] = useState('');
  useEffect(() => {
    const id = setInterval(() => {
      const v = document.querySelector<HTMLVideoElement>('.robot-video video');
      setDims(v && v.videoWidth ? `${v.videoWidth}×${v.videoHeight}` : '');
    }, 2000);
    return () => clearInterval(id);
  }, []);
  if (!dims) return null;
  return <span className="overlay-chip hide-mobile">{dims} · LiveKit</span>;
}

function SnapshotModal({ url, onClose }: { url: string; onClose: () => void }) {
  const t = useT();
  const img = useAuthedImage(url);
  return (
    <Modal
      title={t('robot.snapshot')}
      onClose={onClose}
      wide
      actions={
        <>
          {img.url && (
            <a className="btn" href={img.url} download="snapshot.jpg">
              {t('common.download')}
            </a>
          )}
          <button type="button" className="btn primary" onClick={onClose}>
            {t('common.close')}
          </button>
        </>
      }
    >
      {img.url ? <img src={img.url} alt={t('robot.snapshot')} style={{ width: '100%', borderRadius: 8 }} /> : <div className="empty">{img.error ? t('common.loadFailed') : t('common.loading')}</div>}
    </Modal>
  );
}

function EventLog({ serial }: { serial: string }) {
  const t = useT();
  const log = useLive((s) => s.log[serial]);
  const clear = useLive((s) => s.clearLog);
  const color = (e: RobotLogEntry) =>
    e.kind === 'err' ? 'var(--red-soft)' : e.kind === 'ok' ? 'var(--green)' : e.kind === 'heard' ? 'var(--blue)' : e.kind === 'event' ? (/fail|obstacle|emergency|low/i.test(e.label) ? 'var(--accent)' : 'var(--green)') : 'var(--text-2)';
  return (
    <div className="event-log" aria-live="polite">
      <div className="row" style={{ fontFamily: 'var(--font-body)' }}>
        <span className="section-label">{t('robot.events')}</span>
        <div className="grow" />
        {Boolean(log?.length) && (
          <button type="button" className="btn xs ghost muted" onClick={() => clear(serial)}>
            {t('common.clear')}
          </button>
        )}
      </div>
      {log?.length ? (
        log.map((e) => (
          <div key={e.id}>
            <span className="t">{timeStr(e.t)}</span>
            <span className="lbl" style={{ color: color(e) }}>
              {e.label}
            </span>
            {e.text}
          </div>
        ))
      ) : (
        <div className="muted">{t('robot.noEvents')}</div>
      )}
    </div>
  );
}

function DrivePanel({ serial, online }: { serial: string; online: boolean }) {
  const t = useT();
  const [vMax, setVMax] = useStoredState('cruzr.vMax', 0.4);
  const [wMax, setWMax] = useStoredState('cruzr.wMax', 40);
  const stop = () => {
    void stopRobots([serial]).catch(() => undefined);
    useLive.getState().addLog(serial, 'cmd', '→ STOP');
  };
  const hand = useHandPush(serial);
  const canDrive = online && !hand.enabled;
  const { held, pointerDown, pointerUp, active } = useKeyDrive(serial, { vMax, wMax, enabled: canDrive, onStop: stop });
  const key = (keys: DriveKey[], label: string, aria: string, diag = false) => (
    <button
      type="button"
      className={`dkey${diag ? ' diag' : ''}${keys.every((k) => held.has(k)) ? ' held' : ''}`}
      aria-label={aria}
      disabled={!canDrive}
      onPointerDown={(e) => {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        pointerDown(e.pointerId, keys);
      }}
      onPointerUp={(e) => pointerUp(e.pointerId)}
      onPointerCancel={(e) => pointerUp(e.pointerId)}
      onLostPointerCapture={(e) => pointerUp(e.pointerId)}
      onContextMenu={(e) => e.preventDefault()}
    >
      {label}
    </button>
  );
  const dir = active ? (active.v > 0 ? t('drive.forward') : active.v < 0 ? t('drive.backward') : active.w > 0 ? t('drive.turnLeft') : t('drive.turnRight')) : null;
  return (
    <section className="panel" style={{ gap: 14 }}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h2 className="h2">{t('drive.title')}</h2>
        <span style={{ fontSize: 12, color: 'var(--muted)' }}>{t('drive.keysHint')}</span>
      </div>
      {hand.enabled && (
        <div className="hand-hint" role="note">
          ✋ {t('hand.driveHint')}
        </div>
      )}
      <div className="dpad">
        {key(['fwd', 'left'], '↖', t('drive.fwdLeftAria'), true)}
        {key(['fwd'], 'W', t('drive.fwdAria'))}
        {key(['fwd', 'right'], '↗', t('drive.fwdRightAria'), true)}
        {key(['left'], 'A', t('drive.leftAria'))}
        <button type="button" className="dstop" onClick={stop}>
          {t('drive.stop')}
        </button>
        {key(['right'], 'D', t('drive.rightAria'))}
        {key(['back', 'left'], '↙', t('drive.backLeftAria'), true)}
        {key(['back'], 'S', t('drive.backAria'))}
        {key(['back', 'right'], '↘', t('drive.backRightAria'), true)}
      </div>
      <label className="range-field">
        <span className="top">
          {t('drive.speed')} <span className="val">{vMax.toFixed(2)} m/s</span>
        </span>
        <input type="range" min={0.1} max={1} step={0.05} value={vMax} onChange={(e) => setVMax(Number(e.target.value))} />
        <span className="ends">
          <span>0.1</span>
          <span>{t('drive.maxSpeed')}</span>
        </span>
      </label>
      <label className="range-field">
        <span className="top">
          {t('drive.turn')} <span className="val">{wMax} °/s</span>
        </span>
        <input type="range" min={5} max={60} step={5} value={wMax} onChange={(e) => setWMax(Number(e.target.value))} />
      </label>
      <div className="status-line">
        <span className={`dot ${active ? 'green' : ''}`} />
        {!online ? t('drive.offline') : active ? t('drive.driving', { dir: dir ?? '', v: Math.abs(active.v).toFixed(2), w: Math.abs(Math.round(active.w)) }) : t('drive.idle')}
      </div>
    </section>
  );
}

function SpeechPanel({ serial, online }: { serial: string; online: boolean }) {
  const t = useT();
  const speech = useLive((s) => s.robots[serial]?.status?.speech);
  const heard = useLive((s) => s.heard[serial]);
  const [lang, setLang] = useStoredState<Lang>('cruzr.sayLang', 'ru');
  const [voiceByLang, setVoiceByLang] = useStoredState<Record<string, string>>('cruzr.voices', {});
  const [text, setText] = useState('');
  const [listening, setListening] = useState(false);
  const voice = voiceByLang[lang] ?? '';

  const say = async (e: FormEvent) => {
    e.preventDefault();
    const txt = text.trim();
    if (!txt) return;
    setText('');
    const d = (await sendCmd(serial, 'say', { text: txt, lang, ...(voice ? { voice } : {}) }, `[${lang}${voice ? `/${voice}` : ''}] “${txt}”`)) as { engine?: string } | null;
    if (d?.engine) useLive.getState().addLog(serial, 'info', 'tts', t('speech.saidVia', { engine: d.engine }));
  };
  const listen = async () => {
    setListening(true);
    const d = (await sendCmd(serial, 'listen', { lang })) as { text?: string; lang?: string; engine?: string } | null;
    setListening(false);
    if (d) useLive.getState().setHeard(serial, { text: d.text ?? '', lang: d.lang, engine: d.engine });
  };
  const badge = (what: string, v: string | undefined) => (
    <span className={`tag ${v === 'cloud' ? 'green' : v === 'local' ? 'accent' : 'muted'}`}>
      {what} {v ?? '?'}
    </span>
  );
  return (
    <section className="panel">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h2 className="h2">{t('speech.title')}</h2>
        <span className="row" style={{ gap: 6 }}>
          {badge('TTS', speech?.tts)}
          {badge('STT', speech?.stt)}
        </span>
      </div>
      <div className="row wrap">
        <div role="radiogroup" aria-label={t('speech.language')} className="seg sm">
          {LANGS.map((l) => (
            <button key={l.id} type="button" role="radio" aria-checked={lang === l.id} onClick={() => setLang(l.id)}>
              {l.label}
            </button>
          ))}
        </div>
        <VoicePicker lang={lang} value={voice} onChange={(v) => setVoiceByLang({ ...voiceByLang, [lang]: v })} />
      </div>
      <form className="row" onSubmit={say}>
        <label className="sr-only" htmlFor="say-text">
          {t('speech.textToSay')}
        </label>
        <input id="say-text" className="input lg grow" placeholder={t('speech.placeholder')} value={text} onChange={(e) => setText(e.target.value)} disabled={!online} />
        <button type="submit" className="btn primary lg" disabled={!online || !text.trim()}>
          {t('speech.say')}
        </button>
      </form>
      <div className="row" style={{ gap: 10 }}>
        <button type="button" className="btn lg" onClick={() => void listen()} disabled={!online || listening}>
          <IconWave />
          {listening ? t('speech.listening') : t('speech.listen')}
        </button>
        <span className="grow" style={{ fontSize: 13, lineHeight: 1.4, color: 'var(--text-2)' }}>
          {heard ? (
            <>
              {t('speech.heard')}: “{heard.text || '…'}” <span style={{ color: 'var(--muted-2)' }}>· {[heard.engine, heard.lang].filter(Boolean).join(' · ')}</span>
            </>
          ) : (
            <span className="muted">{t('speech.listenHint')}</span>
          )}
        </span>
      </div>
      <VolumeControl serial={serial} online={online} />
    </section>
  );
}

/** Group emotions by their prefix (techface_, face_, littlestar_…) for the dropdown. */
const emotionGroup = (id: string) => (id.includes('_') ? id.slice(0, id.indexOf('_')) : 'other');

function QuickActions({ serial, online }: { serial: string; online: boolean }) {
  const t = useT();
  const catalog = useCatalog();
  const c = catalog.data;
  const [motion, setMotion] = useState('');
  const [emotion, setEmotion] = useState('');
  const [dance, setDance] = useState('');
  const run = (name: string, args: Record<string, unknown>) => void sendCmd(serial, name, args);
  const quick = QUICK_MOTIONS.filter((q) => !c?.motions.length || c.motions.includes(q.id));
  return (
    <section className="panel">
      <h2 className="h2">{t('quick.title')}</h2>
      <div className="quick-grid">
        {quick.map((q) => (
          <button key={q.id} type="button" className="btn" disabled={!online} onClick={() => run('motion', { id: q.id })}>
            {t(q.key)}
          </button>
        ))}
      </div>
      <div className="row" style={{ alignItems: 'flex-end' }}>
        <label className="field grow">
          {t('quick.motion')}
          <Select ariaLabel={t('quick.motion')} value={motion || c?.motions[0] || ''} onChange={setMotion} options={(c?.motions ?? []).map((m) => ({ value: m, label: m }))} />
        </label>
        <button type="button" className="btn" disabled={!online} onClick={() => run('motion', { id: motion || c?.motions[0] })}>
          {t('quick.play')}
        </button>
      </div>
      <div className="row" style={{ alignItems: 'flex-end' }}>
        <label className="field grow">
          {t('quick.emotion')}
          <Select ariaLabel={t('quick.emotion')} value={emotion || 'techface_smile'} onChange={setEmotion} options={(c?.emotions ?? []).map((m) => ({ value: m, label: m, group: emotionGroup(m) }))} />
        </label>
        <button type="button" className="btn" disabled={!online} onClick={() => run('emotion', { id: emotion || 'techface_smile' })}>
          {t('quick.show')}
        </button>
        <button type="button" className="btn ghost" disabled={!online} onClick={() => run('emotion', { id: null })} title={t('quick.dismiss')}>
          ×
        </button>
      </div>
      <div className="row" style={{ alignItems: 'flex-end' }}>
        <label className="field grow">
          {t('quick.dance')}
          <Select ariaLabel={t('quick.dance')} value={dance || c?.dances[0] || ''} onChange={setDance} options={(c?.dances ?? []).map((m) => ({ value: m, label: m }))} />
        </label>
        <button type="button" className="btn" disabled={!online} onClick={() => run('dance', { id: dance || c?.dances[0] })}>
          {t('quick.play')}
        </button>
      </div>
      <LightControl serial={serial} online={online} />
    </section>
  );
}

function ToursPanel({ serial, online }: { serial: string; online: boolean }) {
  const t = useT();
  const tours = useRobotTours(serial);
  const qc = useQueryClient();
  if (isMissing(tours.error)) return null;
  const run = async (tour: Tour) => {
    useLive.getState().addLog(serial, 'cmd', '→ tour', tour.name);
    try {
      const r = await post<{ results?: PerRobotResult[] }>(`/api/tours/${tour.id}/run`, { robots: [serial] });
      if (r.results) perRobotToast(t('fleet.tourWhat', { name: tour.name }), r.results);
      else toast(t('fleet.tourStarted', { name: tour.name }), 'ok');
    } catch (e) {
      toastErr(e, tour.name);
    }
    void qc.invalidateQueries({ queryKey: qk.robotTours(serial) });
  };
  return (
    <section className="panel">
      <h2 className="h2">{t('robot.tours')}</h2>
      {tours.data?.length ? (
        tours.data.map((tour) => (
          <div key={tour.id} className="row">
            <span className="col grow" style={{ gap: 2, minWidth: 0 }}>
              <span className="ellipsis" style={{ fontWeight: 600 }}>
                {tour.name}
              </span>
              <span className="muted" style={{ fontSize: 12 }}>
                {t('tours.stopsN', { n: tour.stops.length })}
                {tour.lang ? ` · ${tour.lang}` : ''}
                {tour.loop ? ` · ${t('tours.loop')}` : ''}
              </span>
            </span>
            <button type="button" className="btn primary sm" disabled={!online} onClick={() => void run(tour)}>
              {t('tours.run')}
            </button>
          </div>
        ))
      ) : (
        <span className="muted" style={{ fontSize: 13 }}>
          {tours.isLoading ? t('common.loading') : t('robot.noTours')}
        </span>
      )}
    </section>
  );
}
